import React, { useMemo } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';

import { AppsMoreActionsMenu } from './apps/AppsMoreActionsMenu.tsx';
import { platformNavigationIcons } from '../platformNavigationIcons.tsx';

import { useWebItemsWithUrls } from './apps/useWebItemsWithUrls.tsx';
import { useExtensionsWithUrls } from './apps/useExtensionsWithUrls.ts';
import { AppsMenuContent } from './apps/AppsMenuContent.tsx';

const MARKETPLACE_URL = `${CONTEXT_PATH}/marketplace`;

const messages = defineMessages({
	apps: {
		id: 'side-navigation.global.apps',
		defaultMessage: 'Apps',
		description: 'Label text for a menu section that contains third-party apps',
	},
});

export const useAppsItem = (): {
	type: 'expand';
	label: string;
	elemBefore: React.ReactNode;
	isSelected: boolean;
	isExpanded: boolean;
	actionsOnHover: React.JSX.Element | undefined;
	children: React.JSX.Element;
} => {
	const { formatMessage } = useIntl();
	const { accessStatus, edition } = useSessionData();
	const isAdmin = accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS;
	const {
		webItems,
		loading: webItemsLoading,
		error: webItemsError,
	} = useWebItemsWithUrls({ accessStatus, edition });

	const {
		extensions,
		loading: extensionsLoading,
		error: extensionsError,
	} = useExtensionsWithUrls({ accessStatus, edition });

	const isOnAppsRoute = useRouteUrl({
		selector: (routeUrl: string | undefined) => {
			const path = (routeUrl ?? '').split('?')[0].split('#')[0];
			return (
				webItems.some((item) => item.url && path.includes(item.url)) ||
				extensions.some((item) => item.url && path.includes(item.url)) ||
				path.includes(MARKETPLACE_URL)
			);
		},
	});

	const errors = useMemo(() => {
		return [webItemsError, extensionsError].filter((e): e is Error => e != null);
	}, [webItemsError, extensionsError]);

	// Memoised so `useMenuItemsConfig` — and therefore GSN's `items` prop — keeps a stable identity
	// across renders. A fresh object here defeated every downstream memo in the navigation tree
	// (COPPER-1474).
	return useMemo(
		() => ({
			type: 'expand' as const,
			label: formatMessage(messages.apps),
			elemBefore: platformNavigationIcons.apps,
			isSelected: isOnAppsRoute,
			isExpanded: isOnAppsRoute,
			actionsOnHover: isAdmin ? <AppsMoreActionsMenu /> : undefined,
			children: (
				<AppsMenuContent
					loading={webItemsLoading || extensionsLoading}
					errors={errors}
					webItems={webItemsError ? [] : webItems}
					extensions={extensionsError ? [] : extensions}
				/>
			),
		}),
		[
			formatMessage,
			isOnAppsRoute,
			isAdmin,
			webItemsLoading,
			extensionsLoading,
			errors,
			webItems,
			webItemsError,
			extensions,
			extensionsError,
		],
	);
};
